/**
 * <product-info>
 *
 * Owns the variant selection state of a product section: option buttons,
 * variant select, price/compare-price/discount badge, image swapping
 * (desktop + mobile carousel), inventory/stock display, dynamic shipping
 * time text, and the quantity selector (incl. inventory-aware max).
 *
 * Logica 1:1 overgenomen uit het inline script van
 * section-product-standard.liquid (regio: variant + quantity + stock + image swap).
 *
 * Dawn-style data sources:
 *   - <script type="application/json" data-product-json> inside this element
 *     contains {{ product | json }} (no extra network fetch needed).
 *   - <script type="application/json" data-variant-inventory> inside this
 *     element is the section-render-safe variant inventory map. Falls back to
 *     window.productVariantInventory[variantId] for legacy compatibility.
 *   - window.SpiruShipping[sectionId] holds vendor/weekday shipping config.
 *
 * Dawn-style events emitted on `document`:
 *   - variant:change  { sectionId, variant, available, formatMoney }
 *     Apps and other custom elements (sticky-product-bar) listen for this.
 *
 * Required attributes:
 *   data-section          : section id (matches DOM ids)
 *   data-product-url      : product url (used as last-resort fallback fetch)
 *   data-product-vendor   : handleized vendor (for shipping config selection)
 */
class ProductInfo extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.productUrl || '';
    this.vendorHandle = this.dataset.productVendor || '';

    this.productJson = null;
    this.variantInventoryMap = null;
    this.shippingConfig = (window.SpiruShipping && window.SpiruShipping[this.sectionId]) || null;
    this.quantitySelectorInitialized = false;

    this.form = null;
    this.addToCartButton = null;
    this.variantSelect = null;
    this.optionButtons = [];
    this.optionSelects = [];
    this.priceEl = null;
    this.comparePriceEl = null;
    this.discountBadgeEl = null;
    this.stockQuantityEl = null;
    this.iconShoppingBag = '';
    this.iconExclamationCircle = '';
    this.defaultStickyLabel = 'Voeg toe aan winkelwagen';
  }

  connectedCallback() {
    this._resolveForm();
    this._resolveDomRefs();
    if (!this.form) return;

    this._loadVariantInventory();
    this._loadProductJson()
      .then((json) => {
        this.productJson = json;
        this._bindOptionButtons();
        this._bindOptionSelectChange();
        this._applyInitialVariant();
      })
      .catch((error) => console.error('Error loading product JSON:', error));
  }

  _loadVariantInventory() {
    // Prefer the Dawn-style JSON block (section-render safe). Fallback to the
    // legacy window.productVariantInventory global for backward compat.
    const inline = this.querySelector('[data-variant-inventory]');
    if (inline && inline.textContent) {
      try {
        this.variantInventoryMap = JSON.parse(inline.textContent);
        return;
      } catch (e) {
        // ignore, fall back below
      }
    }
    this.variantInventoryMap = window.productVariantInventory || null;
  }

  _getStoredVariant(variantId) {
    if (!variantId) return null;
    if (this.variantInventoryMap && this.variantInventoryMap[variantId]) {
      return this.variantInventoryMap[variantId];
    }
    if (window.productVariantInventory && window.productVariantInventory[variantId]) {
      return window.productVariantInventory[variantId];
    }
    return null;
  }

  /* ------------------------------- DOM lookup ------------------------------ */

  _resolveForm() {
    let form = document.getElementById(`product-form-${this.sectionId}`);
    if (!form) {
      const elementWithDataAttr = document.querySelector(`[data-section-id="${this.sectionId}"]`);
      if (elementWithDataAttr && elementWithDataAttr.tagName === 'FORM') {
        form = elementWithDataAttr;
      } else if (elementWithDataAttr) {
        form = elementWithDataAttr.closest('form');
      }
    }
    if (!form) form = document.querySelector('form[action*="/cart/add"]');
    if (!form) {
      const submitButton = document.getElementById(`product-submit-${this.sectionId}`);
      if (submitButton) form = submitButton.closest('form');
    }
    if (!form) {
      const optionSelects = document.querySelectorAll('[data-product-option-select]');
      if (optionSelects.length > 0) form = optionSelects[0].closest('form');
    }
    this.form = form;
  }

  _resolveDomRefs() {
    if (!this.form) return;
    this.addToCartButton = document.getElementById(`product-submit-${this.sectionId}`);
    this.variantSelect = document.getElementById(`variant-select-${this.sectionId}`);
    this.optionButtons = this.form.querySelectorAll('[data-product-option]:not(select)');
    this.optionSelects = this.form.querySelectorAll('[data-product-option-select]');
    this.priceEl = document.getElementById(`price-${this.sectionId}`);
    this.comparePriceEl = this.form.querySelector('[data-compare-price]');
    this.discountBadgeEl = this.form.querySelector('[data-discount-badge]');
    this.stockQuantityEl = document.getElementById(`stock-quantity-${this.sectionId}`);

    const iconShoppingBagTpl = document.getElementById(`icon-shopping-bag-${this.sectionId}`);
    const iconExclamationTpl = document.getElementById(`icon-exclamation-circle-${this.sectionId}`);
    this.iconShoppingBag = iconShoppingBagTpl ? iconShoppingBagTpl.innerHTML.trim() : '';
    this.iconExclamationCircle = iconExclamationTpl ? iconExclamationTpl.innerHTML.trim() : '';

    const stickyHeader = document.getElementById(`sticky-product-header-${this.sectionId}`);
    const stickyBtn = stickyHeader ? stickyHeader.querySelector('[data-sticky-add-to-cart]') : null;
    this.defaultStickyLabel = stickyBtn?.dataset.stickyLabel || 'In winkelwagen';
  }

  _loadProductJson() {
    const inline = this.querySelector('[data-product-json]');
    if (inline && inline.textContent) {
      try {
        return Promise.resolve(JSON.parse(inline.textContent));
      } catch (e) {
        // fall through to fetch
      }
    }
    if (!this.productUrl) return Promise.resolve(null);
    return fetch(`${this.productUrl}.json`)
      .then((r) => r.json())
      .then((data) => data.product || null);
  }

  /* ------------------------------- helpers --------------------------------- */

  formatMoney(cents) {
    // Standaard input = Shopify cents (zoals Liquid `| json` en /cart/add.js
    // teruggeven). Legacy fallback: als de waarde een string-decimaal is uit
    // /products/<handle>.json ("1.99"), behandel die als euros.
    if (cents == null || cents === '') return '';
    if (typeof cents === 'string') {
      // Strings uit /products/<handle>.json zijn euros met punt, bv. "1.99"
      const asFloat = parseFloat(cents);
      if (isNaN(asFloat)) return '';
      return '€' + asFloat.toFixed(2).replace('.', ',');
    }
    const numCents = Number(cents);
    if (isNaN(numCents)) return '';
    return '€' + (numCents / 100).toFixed(2).replace('.', ',');
  }

  toggleLoadingState(isLoading) {
    const stickyHeader = document.getElementById(`sticky-product-header-${this.sectionId}`);
    const stickyAddBtn =
      stickyHeader?.querySelector('[data-sticky-add-to-cart]') ||
      stickyHeader?.querySelector('button[onclick*="product-submit"]');
    const buttons = [this.addToCartButton];
    if (stickyAddBtn) buttons.push(stickyAddBtn);

    buttons.forEach((button) => {
      if (!button) return;
      if (isLoading) {
        button.dataset.prevDisabled = button.hasAttribute('disabled') ? 'true' : 'false';
        button.classList.add('loading');
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-busy', 'true');
      } else {
        button.classList.remove('loading');
        button.removeAttribute('aria-busy');
        if (button.dataset.prevDisabled === 'false') {
          button.removeAttribute('disabled');
        }
        delete button.dataset.prevDisabled;
      }
    });
  }

  /* ------------------------- quantity selector ----------------------------- */

  updateQuantitySelector(inventoryQuantity, inventoryManagement, inventoryPolicy) {
    const quantityInput = document.getElementById(`quantity-${this.sectionId}`);
    const quantitySelector = document.querySelector(
      `[data-quantity-selector][data-section-id="${this.sectionId}"]`
    );
    if (!quantityInput || !quantitySelector) return;

    const decreaseBtn = quantitySelector.querySelector('[data-quantity-decrease]');
    const increaseBtn = quantitySelector.querySelector('[data-quantity-increase]');

    let maxQuantity = null;
    const isInventoryTracked =
      inventoryManagement !== null && inventoryManagement !== '' && inventoryManagement !== undefined;
    const allowsBackorder = inventoryPolicy === 'continue';

    if (isInventoryTracked && !allowsBackorder && inventoryQuantity !== null && inventoryQuantity !== undefined) {
      const invQty = parseInt(inventoryQuantity, 10);
      if (!isNaN(invQty) && invQty > 0) maxQuantity = invQty;
    }

    if (maxQuantity !== null) {
      quantityInput.setAttribute('max', maxQuantity.toString());
      quantityInput.setAttribute('data-max-quantity', maxQuantity.toString());
    } else {
      quantityInput.removeAttribute('max');
      quantityInput.removeAttribute('data-max-quantity');
    }

    let currentValue = parseInt(quantityInput.value, 10) || 1;
    if (maxQuantity !== null && currentValue > maxQuantity) {
      quantityInput.value = maxQuantity.toString();
      currentValue = maxQuantity;
    }

    if (decreaseBtn) decreaseBtn.disabled = currentValue <= 1;
    if (increaseBtn) {
      if (maxQuantity !== null) {
        increaseBtn.disabled = currentValue >= maxQuantity;
      } else {
        increaseBtn.disabled = false;
      }
    }

    const errorEl = document.getElementById(`quantity-error-${this.sectionId}`);
    if (errorEl) errorEl.classList.add('hidden');
  }

  initQuantitySelector() {
    if (this.quantitySelectorInitialized) return;

    const quantitySelector = document.querySelector(
      `[data-quantity-selector][data-section-id="${this.sectionId}"]`
    );
    if (!quantitySelector) return;

    const quantityInput = document.getElementById(`quantity-${this.sectionId}`);
    const decreaseBtn = quantitySelector.querySelector('[data-quantity-decrease]');
    const increaseBtn = quantitySelector.querySelector('[data-quantity-increase]');
    if (!quantityInput || !decreaseBtn || !increaseBtn) return;

    const sectionId = this.sectionId;
    const updateQuantityButtons = () => {
      const value = parseInt(quantityInput.value, 10) || 1;
      const maxQty = quantityInput.getAttribute('data-max-quantity');
      const maxQuantity = maxQty ? parseInt(maxQty, 10) : null;
      decreaseBtn.disabled = value <= 1;
      increaseBtn.disabled = maxQuantity !== null && value >= maxQuantity;
    };

    decreaseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const currentValue = parseInt(quantityInput.value, 10) || 1;
      if (currentValue > 1) {
        quantityInput.value = (currentValue - 1).toString();
        updateQuantityButtons();
      }
    });

    increaseBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const currentValue = parseInt(quantityInput.value, 10) || 1;
      const maxQty = quantityInput.getAttribute('data-max-quantity');
      const maxQuantity = maxQty ? parseInt(maxQty, 10) : null;

      if (maxQuantity !== null && currentValue >= maxQuantity) {
        const errorEl = document.getElementById(`quantity-error-${sectionId}`);
        const errorMsgEl = errorEl?.querySelector('[data-quantity-error-message]');
        if (errorEl && errorMsgEl) {
          errorMsgEl.textContent = `Je kunt maximaal ${maxQuantity} ${maxQuantity === 1 ? 'stuk' : 'stukken'} toevoegen (er zijn er ${maxQuantity} op voorraad).`;
          errorEl.classList.remove('hidden');
          setTimeout(() => errorEl.classList.add('hidden'), 4000);
        }
        return;
      }

      const errorEl = document.getElementById(`quantity-error-${sectionId}`);
      if (errorEl) errorEl.classList.add('hidden');

      const newValue = currentValue + 1;
      quantityInput.value = newValue.toString();
      updateQuantityButtons();
      if (maxQuantity !== null && newValue >= maxQuantity) {
        increaseBtn.disabled = true;
      }
    });

    quantityInput.addEventListener('change', () => {
      const value = parseInt(quantityInput.value, 10) || 1;
      const maxQty = quantityInput.getAttribute('data-max-quantity');
      const maxQuantity = maxQty ? parseInt(maxQty, 10) : null;

      if (value < 1) {
        quantityInput.value = '1';
      } else if (maxQuantity !== null && value > maxQuantity) {
        quantityInput.value = maxQuantity.toString();
        const errorEl = document.getElementById(`quantity-error-${sectionId}`);
        const errorMsgEl = errorEl?.querySelector('[data-quantity-error-message]');
        if (errorEl && errorMsgEl) {
          errorMsgEl.textContent = `Je kunt maximaal ${maxQuantity} ${maxQuantity === 1 ? 'stuk' : 'stukken'} toevoegen (er zijn er ${maxQuantity} op voorraad).`;
          errorEl.classList.remove('hidden');
          setTimeout(() => errorEl.classList.add('hidden'), 4000);
        }
      } else {
        const errorEl = document.getElementById(`quantity-error-${sectionId}`);
        if (errorEl) errorEl.classList.add('hidden');
      }
      updateQuantityButtons();
    });

    this.quantitySelectorInitialized = true;
  }

  /* ------------------------------ shipping --------------------------------- */

  getShippingTime(quantity, inventoryMgmt, inventoryPol) {
    const cfg = this.shippingConfig || { default: { inStock: '', backorder: '' } };

    let vendorKey = 'default';
    if (this.vendorHandle && typeof this.vendorHandle === 'string') {
      const candidates = ['phoenix-import', 'easter-trading', 'ruben-robijn', 'something-different', 'nemesis-now'];
      for (const c of candidates) {
        if (this.vendorHandle.includes(c) && cfg[c]) {
          vendorKey = c;
          break;
        }
      }
    }

    const cfgBase = cfg[vendorKey] || cfg.default;
    const day = new Date().getDay();
    let dayKey = null;
    if (day === 4) dayKey = 'thu';
    else if (day === 5) dayKey = 'fri';
    else if (day === 6) dayKey = 'sat';
    else if (day === 0) dayKey = 'sun';

    const dayCfg = dayKey && cfgBase[dayKey] ? cfgBase[dayKey] : null;
    const shippingTimeInStock = dayCfg && dayCfg.inStock ? dayCfg.inStock : cfgBase.inStock;
    const shippingTimeBackorder = dayCfg && dayCfg.backorder ? dayCfg.backorder : cfgBase.backorder;

    let qty = null;
    if (quantity !== null && quantity !== undefined && quantity !== '') {
      qty = typeof quantity === 'string' ? parseInt(quantity, 10) : Number(quantity);
      if (isNaN(qty)) qty = null;
    }

    const isInventoryTracked = inventoryMgmt !== null && inventoryMgmt !== '' && inventoryMgmt !== undefined;
    let isInStock = false;
    let isBackorder = false;
    if (isInventoryTracked) {
      if (qty !== null && qty > 0) isInStock = true;
      else if (inventoryPol === 'continue') isBackorder = true;
    } else {
      isInStock = true;
    }

    if (isInStock) return shippingTimeInStock;
    if (isBackorder) return shippingTimeBackorder;
    return shippingTimeInStock;
  }

  /* ----------------------------- updateVariant ----------------------------- */

  updateVariant(variant) {
    if (!variant) {
      if (this.addToCartButton) {
        this.addToCartButton.setAttribute('disabled', 'disabled');
        const iconUnavailable = this.iconExclamationCircle || this.addToCartButton.dataset.iconUnavailable || '';
        const textUnavailable = this.addToCartButton.dataset.textUnavailable || 'Niet op voorraad';
        this.addToCartButton.innerHTML = iconUnavailable + textUnavailable;
      }
      this._dispatchVariantChange(null, false);
      return;
    }

    if (this.variantSelect) this.variantSelect.value = variant.id.toString();

    this._swapImage('main-product-image', 'product-image-default', 'product-image-variant', variant.id);
    this._swapMobileCarouselImage(variant.id);

    if (this.priceEl) this.priceEl.textContent = this.formatMoney(variant.price);

    const variantPriceNumber = variant.price != null ? Number(variant.price) : null;
    const compareAtPriceNumber = variant.compare_at_price != null ? Number(variant.compare_at_price) : null;
    const hasSale =
      compareAtPriceNumber != null &&
      !Number.isNaN(compareAtPriceNumber) &&
      variantPriceNumber != null &&
      !Number.isNaN(variantPriceNumber) &&
      compareAtPriceNumber > variantPriceNumber;

    if (this.comparePriceEl) {
      if (hasSale) {
        this.comparePriceEl.style.display = '';
        this.comparePriceEl.textContent = this.formatMoney(compareAtPriceNumber);
      } else {
        this.comparePriceEl.style.display = 'none';
        this.comparePriceEl.textContent = '';
      }
    }

    if (this.discountBadgeEl) {
      if (hasSale) {
        const discountPercent = Math.round(100 - (variantPriceNumber * 100.0) / compareAtPriceNumber);
        this.discountBadgeEl.style.display = '';
        this.discountBadgeEl.textContent = `-${discountPercent}%`;
      } else {
        this.discountBadgeEl.style.display = 'none';
        this.discountBadgeEl.textContent = '';
      }
    }

    // Availability check, prefer stored fallback when variant fields missing.
    let variantAvailable = variant.available;
    let variantInventoryManagement = variant.inventory_management;
    let variantInventoryQuantity = variant.inventory_quantity;
    let variantInventoryPolicy = variant.inventory_policy;

    if ((variantAvailable === null || variantAvailable === undefined) && variant.id) {
      const storedData = this._getStoredVariant(variant.id);
      if (storedData) {
        if (variantAvailable === null || variantAvailable === undefined) variantAvailable = storedData.available;
        if (!variantInventoryManagement && storedData.inventory_management)
          variantInventoryManagement = storedData.inventory_management;
        if (
          (variantInventoryQuantity === null || variantInventoryQuantity === undefined) &&
          storedData.inventory_quantity !== null
        )
          variantInventoryQuantity = storedData.inventory_quantity;
        if (!variantInventoryPolicy && storedData.inventory_policy)
          variantInventoryPolicy = storedData.inventory_policy;
      }
    }

    let isAvailable = false;
    if (variantAvailable === true || variantAvailable === 'true') {
      isAvailable = true;
    } else if (
      variantInventoryManagement === null ||
      variantInventoryManagement === '' ||
      variantInventoryManagement === undefined
    ) {
      isAvailable = true;
    } else if (variantInventoryPolicy === 'continue') {
      isAvailable = true;
    } else if (
      variantInventoryQuantity !== null &&
      variantInventoryQuantity !== undefined &&
      variantInventoryQuantity > 0
    ) {
      isAvailable = true;
    }

    // Stock display update (incl. shipping text)
    let inventoryQuantity = variant.inventory_quantity;
    let inventoryManagement = variant.inventory_management;
    let inventoryPolicy = variant.inventory_policy;
    if (variant.id) {
      const storedData = this._getStoredVariant(variant.id);
      if (storedData) {
        if (inventoryQuantity === null || inventoryQuantity === undefined)
          inventoryQuantity = storedData.inventory_quantity;
        if (!inventoryManagement && storedData.inventory_management)
          inventoryManagement = storedData.inventory_management;
        if (!inventoryPolicy && storedData.inventory_policy) inventoryPolicy = storedData.inventory_policy;
      }
    }

    if (this.stockQuantityEl) {
      this._updateStockDisplay(inventoryQuantity, inventoryManagement, inventoryPolicy);
    }

    this.updateQuantitySelector(inventoryQuantity, inventoryManagement, inventoryPolicy);

    // Reset quantity to 1 if it exceeds new max (matches legacy)
    const quantityInput = document.getElementById(`quantity-${this.sectionId}`);
    if (quantityInput) {
      const currentQty = parseInt(quantityInput.value, 10) || 1;
      const maxQty = quantityInput.getAttribute('data-max-quantity');
      const maxQuantity = maxQty ? parseInt(maxQty, 10) : null;
      if (maxQuantity !== null && currentQty > maxQuantity) {
        quantityInput.value = '1';
        this.updateQuantitySelector(inventoryQuantity, inventoryManagement, inventoryPolicy);
      }
    }

    if (this.addToCartButton) {
      if (isAvailable) {
        this.addToCartButton.removeAttribute('disabled');
        const textAvailable = this.addToCartButton.dataset.textAvailable || 'In winkelwagen';
        this.addToCartButton.innerHTML = (this.iconShoppingBag || '') + textAvailable;
      } else {
        this.addToCartButton.setAttribute('disabled', 'disabled');
        const textUnavailable = this.addToCartButton.dataset.textUnavailable || 'Niet op voorraad';
        this.addToCartButton.innerHTML = (this.iconExclamationCircle || '') + textUnavailable;
      }
    }

    this._dispatchVariantChange(variant, isAvailable);
  }

  _dispatchVariantChange(variant, isAvailable) {
    const detail = {
      sectionId: this.sectionId,
      variant,
      available: isAvailable,
      formatMoney: this.formatMoney.bind(this),
    };
    document.dispatchEvent(new CustomEvent('variant:change', { detail }));
    this.dispatchEvent(new CustomEvent('variant:change', { detail, bubbles: true }));

    // Dawn 2.0+ PUB_SUB bus bridge (optional shim from shopify-compat.js).
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.variantChange, detail);
    }

    // Legacy Shopify "variantChange" event on the hidden variant select +
    // form. Older apps (jQuery-era selectors) still hook into this.
    if (variant) {
      if (this.variantSelect) {
        this.variantSelect.dispatchEvent(
          new CustomEvent('variantChange', { detail: { variant }, bubbles: true })
        );
      }
      if (this.form) {
        this.form.dispatchEvent(
          new CustomEvent('variantChange', { detail: { variant }, bubbles: true })
        );
      }
    }
  }

  _swapImage(containerId, defaultClass, variantClass, variantId) {
    const container = document.getElementById(`${containerId}-${this.sectionId}`);
    if (!container) return;
    const defaultImage = container.querySelector(`.${defaultClass}`);
    const variantImages = container.querySelectorAll(`.${variantClass}`);
    const currentVariantImage = container.querySelector(`[data-variant-id="${variantId}"]`);

    variantImages.forEach((img) => {
      img.style.display = 'none';
      img.style.opacity = '0';
    });

    if (currentVariantImage) {
      if (defaultImage) {
        defaultImage.style.opacity = '0';
        defaultImage.style.pointerEvents = 'none';
      }
      currentVariantImage.style.display = 'block';
      currentVariantImage.style.position = 'absolute';
      currentVariantImage.style.top = '0';
      currentVariantImage.style.left = '0';
      currentVariantImage.style.width = '100%';
      currentVariantImage.style.height = '100%';
      setTimeout(() => {
        currentVariantImage.style.opacity = '1';
      }, 10);
    } else if (defaultImage) {
      defaultImage.style.opacity = '1';
      defaultImage.style.pointerEvents = '';
    }
  }

  _swapMobileCarouselImage(variantId) {
    const mobileCarousel = document.querySelector('.product-media-carousel');
    if (!mobileCarousel) return;
    const firstSlide = mobileCarousel.querySelector('.product-media-carousel-slide[data-slide-index="0"]');
    if (!firstSlide) return;

    const defaultImageMobile = firstSlide.querySelector('.product-image-default-mobile');
    const variantImagesMobile = firstSlide.querySelectorAll('.product-image-variant-mobile');
    const currentVariantImageMobile = firstSlide.querySelector(`[data-variant-id="${variantId}"]`);

    variantImagesMobile.forEach((img) => {
      img.style.display = 'none';
      img.style.opacity = '0';
    });

    if (currentVariantImageMobile) {
      if (defaultImageMobile) {
        defaultImageMobile.style.opacity = '0';
        defaultImageMobile.style.pointerEvents = 'none';
      }
      currentVariantImageMobile.style.display = 'block';
      currentVariantImageMobile.style.position = 'absolute';
      currentVariantImageMobile.style.top = '0';
      currentVariantImageMobile.style.left = '0';
      currentVariantImageMobile.style.width = '100%';
      currentVariantImageMobile.style.height = '100%';
      setTimeout(() => {
        currentVariantImageMobile.style.opacity = '1';
      }, 10);
    } else if (defaultImageMobile) {
      defaultImageMobile.style.opacity = '1';
      defaultImageMobile.style.pointerEvents = '';
    }
  }

  _updateStockDisplay(quantity, inventoryMgmt, inventoryPol) {
    // Cadeaukaart-template heeft geen voorraadblok.
    if (!this.stockQuantityEl) return;

    // Vakantiemodus: server-rendered override — skip dynamic stock/shipping updates
    if (this.stockQuantityEl.hasAttribute('data-vacation-stock-override')) {
      return;
    }

    let qty = null;
    if (quantity !== null && quantity !== undefined && quantity !== '') {
      qty = typeof quantity === 'string' ? parseInt(quantity, 10) : Number(quantity);
      if (isNaN(qty)) qty = null;
    }

    const hasValidQuantity = qty !== null && !isNaN(qty) && typeof qty === 'number';
    const isInventoryTracked = inventoryMgmt !== null && inventoryMgmt !== '' && inventoryMgmt !== undefined;
    const shouldHideStockQty = (hasValidQuantity && qty > 20) || inventoryPol === 'continue';

    this.stockQuantityEl.style.display = '';

    const stockP = this.stockQuantityEl.querySelector('[data-stock-quantity]')?.closest('p') || null;
    if (stockP) {
      if (isInventoryTracked && hasValidQuantity && qty >= 0 && !shouldHideStockQty) {
        const span = stockP.querySelector('[data-stock-quantity]');
        if (span) span.textContent = qty;
        stockP.style.display = '';
      } else {
        stockP.style.display = 'none';
      }
    }

    const shippingP = this.stockQuantityEl.querySelector('[data-shipping-time]');
    if (shippingP) {
      const text = this.getShippingTime(quantity, inventoryMgmt, inventoryPol);
      if (text && typeof text === 'string') shippingP.textContent = text;
    }
  }

  /* ------------------------- option button logic --------------------------- */

  getSelectedOptions() {
    return Array.from(this.optionSelects)
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((select) => select.value);
  }

  getVariantFromOptions() {
    if (!this.productJson || !this.productJson.variants) return null;
    const selectedOptions = this.getSelectedOptions();

    let variant = this.productJson.variants.find((v) => {
      if (!v) return false;
      const variantOptions = [v.option1, v.option2, v.option3].filter(
        (opt) => opt !== null && opt !== undefined && opt !== ''
      );
      if (variantOptions.length === selectedOptions.length) {
        return variantOptions.every((value, idx) => String(value) === String(selectedOptions[idx]));
      }
      return false;
    });

    if (!variant && this.variantSelect && this.variantSelect.value) {
      variant = this.productJson.variants.find((v) => String(v.id) === String(this.variantSelect.value));
    }
    return variant;
  }

  checkOptionAvailability(optionPosition, optionValue, currentSelections) {
    if (!this.productJson || !this.productJson.variants) return false;

    const testSelections = [...currentSelections];
    testSelections[optionPosition - 1] = optionValue;
    const hasAllOtherSelections = testSelections.every(
      (sel, idx) => idx === optionPosition - 1 || (sel !== null && sel !== undefined && sel !== '')
    );

    const matchingVariants = this.productJson.variants.filter((variant) => {
      if (!variant) return false;
      const variantOptions = [variant.option1, variant.option2, variant.option3].filter(
        (opt) => opt !== null && opt !== undefined && opt !== ''
      );
      const optionIndex = optionPosition - 1;
      if (variantOptions[optionIndex] !== optionValue) return false;
      if (hasAllOtherSelections && variantOptions.length === testSelections.length) {
        return variantOptions.every((value, idx) => {
          if (idx === optionIndex) return true;
          return String(value) === String(testSelections[idx]);
        });
      }
      return true;
    });

    for (const variant of matchingVariants) {
      let variantAvailable = variant.available;
      let variantInventoryManagement = variant.inventory_management;
      let variantInventoryQuantity = variant.inventory_quantity;
      let variantInventoryPolicy = variant.inventory_policy;

      if ((variantAvailable === null || variantAvailable === undefined) && variant.id) {
        const storedData = this._getStoredVariant(variant.id);
        if (storedData) {
          if (variantAvailable === null || variantAvailable === undefined) variantAvailable = storedData.available;
          if (!variantInventoryManagement && storedData.inventory_management)
            variantInventoryManagement = storedData.inventory_management;
          if (
            (variantInventoryQuantity === null || variantInventoryQuantity === undefined) &&
            storedData.inventory_quantity !== null
          )
            variantInventoryQuantity = storedData.inventory_quantity;
        }
      }

      let isAvailable = false;
      if (variantAvailable === true || variantAvailable === 'true') isAvailable = true;
      else if (
        variantInventoryManagement === null ||
        variantInventoryManagement === '' ||
        variantInventoryManagement === undefined
      )
        isAvailable = true;
      else if (variantInventoryPolicy === 'continue') isAvailable = true;
      else if (
        variantInventoryQuantity !== null &&
        variantInventoryQuantity !== undefined &&
        variantInventoryQuantity > 0
      )
        isAvailable = true;

      if (isAvailable) return true;
    }
    return false;
  }

  updateOptionButtons() {
    if (!this.productJson || !this.productJson.variants) return;
    const currentSelections = this.getSelectedOptions();

    this.optionButtons.forEach((button) => {
      const optionPosition = Number(button.dataset.optionPosition);
      const optionValue = button.dataset.optionValue;
      const isSelected = currentSelections[optionPosition - 1] === optionValue;
      const isAvailable = this.checkOptionAvailability(optionPosition, optionValue, currentSelections);

      if (isSelected) {
        button.classList.remove(
          'bg-accent-light',
          'border-gray-300',
          'text-gray-900',
          'hover:border-primary',
          'hover:bg-accent',
          'bg-gray-100',
          'border-gray-200',
          'text-gray-400',
          'cursor-not-allowed',
          'opacity-60'
        );
        button.classList.add('bg-primary', 'text-white', 'border-primary');
        button.setAttribute('aria-pressed', 'true');
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
      } else if (isAvailable) {
        button.classList.remove(
          'bg-primary',
          'text-white',
          'border-primary',
          'bg-gray-100',
          'border-gray-200',
          'text-gray-400',
          'cursor-not-allowed',
          'opacity-60'
        );
        button.classList.add(
          'bg-accent-light',
          'border-gray-300',
          'text-gray-900',
          'hover:border-primary',
          'hover:bg-accent'
        );
        button.setAttribute('aria-pressed', 'false');
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
      } else {
        button.classList.remove(
          'bg-primary',
          'text-white',
          'border-primary',
          'bg-accent-light',
          'border-gray-300',
          'text-gray-900',
          'hover:border-primary',
          'hover:bg-accent'
        );
        button.classList.add('bg-gray-100', 'border-gray-200', 'text-gray-400', 'cursor-not-allowed', 'opacity-60');
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
      }
    });
  }

  _bindOptionButtons() {
    this.updateOptionButtons();
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.hasAttribute('disabled')) return;
        const optionPosition = Number(button.dataset.optionPosition);
        const optionValue = button.dataset.optionValue;
        const hiddenSelect = document.getElementById(`option-${this.sectionId}-${optionPosition}`);
        if (hiddenSelect) hiddenSelect.value = optionValue;
        const variant = this.getVariantFromOptions();
        if (variant && this.variantSelect) this.variantSelect.value = variant.id.toString();
        this.updateOptionButtons();
        this.updateVariant(variant);
      });
    });
  }

  _bindOptionSelectChange() {
    this.optionSelects.forEach((select) => {
      select.addEventListener('change', () => {
        const variant = this.getVariantFromOptions();
        if (variant && this.variantSelect) this.variantSelect.value = variant.id.toString();
        this.updateOptionButtons();
        this.updateVariant(variant);
      });
    });
  }

  _applyInitialVariant() {
    let initialVariant = null;
    if (this.optionSelects.length > 0) {
      initialVariant = this.getVariantFromOptions();
    } else if (this.variantSelect && this.variantSelect.value) {
      initialVariant = this.productJson.variants.find((v) => String(v.id) === String(this.variantSelect.value));
    } else {
      const hiddenVariantInput = this.form.querySelector('input[name="id"][type="hidden"]');
      if (hiddenVariantInput && hiddenVariantInput.value) {
        initialVariant = this.productJson.variants.find((v) => String(v.id) === String(hiddenVariantInput.value));
      }
      if (!initialVariant && this.productJson.variants && this.productJson.variants.length > 0) {
        initialVariant = this.productJson.variants[0];
      }
    }

    const applyOne = (variant) => {
      this.updateOptionButtons();
      this.updateVariant(variant);

      let invQty = variant.inventory_quantity;
      let invMgmt = variant.inventory_management;
      let invPolicy = variant.inventory_policy;
      if (variant.id) {
        const stored = this._getStoredVariant(variant.id);
        if (stored) {
          if (invQty === null || invQty === undefined) invQty = stored.inventory_quantity;
          if (!invMgmt) invMgmt = stored.inventory_management;
          if (!invPolicy) invPolicy = stored.inventory_policy;
        }
      }
      this.updateQuantitySelector(invQty, invMgmt, invPolicy);
    };

    if (initialVariant) {
      applyOne(initialVariant);
    } else if (
      this.optionSelects.length === 0 &&
      this.productJson.variants &&
      this.productJson.variants.length === 1
    ) {
      const singleVariant = this.productJson.variants[0];
      if (singleVariant.id) {
        const storedData = this._getStoredVariant(singleVariant.id);
        if (storedData) {
          if (!singleVariant.available && storedData.available !== undefined)
            singleVariant.available = storedData.available;
          if (!singleVariant.inventory_management && storedData.inventory_management)
            singleVariant.inventory_management = storedData.inventory_management;
          if (
            (singleVariant.inventory_quantity === null || singleVariant.inventory_quantity === undefined) &&
            storedData.inventory_quantity !== null
          )
            singleVariant.inventory_quantity = storedData.inventory_quantity;
        }
      }
      applyOne(singleVariant);
    }

    this.initQuantitySelector();
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
